// RegistrationTrendChart.jsx - FIXED TO USE REAL API DATA
import React, { useState, useRef, useEffect, useMemo } from "react";
import { TrendingUp } from "lucide-react";

// ✅ Accept data as prop instead of hardcoded values
const RegistrationTrendChart = ({ data = [] }) => {
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, data: null });
  const [isMobile, setIsMobile] = useState(false);
  const chartRef = useRef(null);
  const tooltipRef = useRef(null);

  // ✅ Process real API data
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return { dates: [], values: [], maxValue: 0, totalCount: 0 };
    
    // Sort by date and extract values
    const sorted = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Format dates for display: "Feb 20" or "Jan 13"
    const dates = sorted.map(d => {
      const dateObj = new Date(d.date);
      return dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });
    
    const values = sorted.map(d => d.count || 0);
    const maxValue = Math.max(...values, 1) * 1.1; // Add 10% buffer
    const totalCount = values.reduce((sum, v) => sum + v, 0);
    
    return { dates, values, maxValue, totalCount };
  }, [data]);

  // Responsive configuration
  const PADDING = { top: 30, right: 20, bottom: 50, left: 50 };
  const MAX_MOBILE_WIDTH = 768;
  const TOOLTIP_OFFSET = 15;
  const MIN_DIST_THRESHOLD = 25;

  // Detect mobile view
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= MAX_MOBILE_WIDTH);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Calculate chart dimensions and positions
  const getChartDimensions = () => {
    if (!chartRef.current) return { width: 600, height: 300, innerWidth: 530, innerHeight: 220 };
    
    const width = chartRef.current.clientWidth;
    const height = Math.max(250, width * 0.5);
    const innerWidth = width - PADDING.left - PADDING.right;
    const innerHeight = height - PADDING.top - PADDING.bottom;
    
    return { width, height, innerWidth, innerHeight };
  };

  const { width, height, innerWidth, innerHeight } = getChartDimensions();
  const { dates, values, maxValue, totalCount } = chartData;
  
  const stepSize = Math.max(1, Math.floor(maxValue / 5));
  
  // Generate grid lines and labels
  const gridLines = [];
  const yLabels = [];
  for (let i = 0; i <= 5; i++) {
    const yValue = maxValue - (i * (maxValue / 5));
    const yPos = PADDING.top + (innerHeight * (i / 5));
    
    gridLines.push(
      <line 
        key={`grid-${i}`} 
        x1={PADDING.left} 
        y1={yPos} 
        x2={width - PADDING.right} 
        y2={yPos} 
        stroke="#e2e8f0" 
        strokeWidth="1" 
        strokeDasharray="4,4"
      />
    );
    
    yLabels.push(
      <text 
        key={`label-${i}`} 
        x={PADDING.left - 12} 
        y={yPos + 4} 
        fontSize="11" 
        fill="#64748b" 
        textAnchor="end"
        fontFamily="Inter, -apple-system, sans-serif"
      >
        {Math.round(yValue)}
      </text>
    );
  }

  // Generate data points and path - ✅ Using real data
  const points = dates.map((date, i) => {
    const x = PADDING.left + (i * (innerWidth / Math.max(1, dates.length - 1)));
    const y = PADDING.top + innerHeight - (values[i] / maxValue) * innerHeight;
    return { x, y, value: values[i], date };
  });

  const pathD = points.length > 0 ? `M ${points.map(p => `${p.x},${p.y}`).join(' L ')}` : '';
  
  // Handle mouse interactions
  const handleMouseMove = (e) => {
    if (!chartRef.current || isMobile || dates.length === 0) return;
    
    const rect = chartRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Find closest point
    let closestPoint = null;
    let minDist = Infinity;
    
    points.forEach(point => {
      const dist = Math.abs(mouseX - point.x);
      if (dist < minDist && dist < MIN_DIST_THRESHOLD) {
        minDist = dist;
        closestPoint = point;
      }
    });
    
    if (closestPoint) {
      const tooltipWidth = tooltipRef.current?.offsetWidth || 150;
      const tooltipHeight = tooltipRef.current?.offsetHeight || 60;
      
      let tooltipX = mouseX + TOOLTIP_OFFSET;
      let tooltipY = mouseY - tooltipHeight - TOOLTIP_OFFSET;
      
      if (tooltipX + tooltipWidth > rect.width - 10) {
        tooltipX = mouseX - tooltipWidth - TOOLTIP_OFFSET;
      }
      if (tooltipY < 10) tooltipY = 10;
      if (tooltipY + tooltipHeight > rect.height - 10) {
        tooltipY = rect.height - tooltipHeight - 10;
      }
      
      setTooltip({
        visible: true,
        x: tooltipX,
        y: tooltipY,
        data: { date: closestPoint.date, value: closestPoint.value }
      });
    } else {
      setTooltip(prev => prev.visible ? { ...prev, visible: false } : prev);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) setTooltip({ visible: false, x: 0, y: 0, data: null });
  };

  // Mobile touch handling
  const handleTouchStart = (e) => {
    if (!chartRef.current || dates.length === 0) return;
    
    const rect = chartRef.current.getBoundingClientRect();
    const touchX = e.touches[0].clientX - rect.left;
    
    let closestPoint = points[0];
    let minDist = Math.abs(touchX - points[0].x);
    
    points.forEach(point => {
      const dist = Math.abs(touchX - point.x);
      if (dist < minDist) {
        minDist = dist;
        closestPoint = point;
      }
    });
    
    if (minDist < 40) {
      const tooltipWidth = 160;
      const tooltipX = Math.min(
        Math.max(closestPoint.x - tooltipWidth / 2, PADDING.left),
        width - PADDING.right - tooltipWidth
      );
      
      setTooltip({
        visible: true,
        x: tooltipX,
        y: PADDING.top + 20,
        data: { date: closestPoint.date, value: closestPoint.value }
      });
    }
  };

  const handleTouchEnd = () => {
    if (isMobile) setTooltip({ visible: false, x: 0, y: 0, data: null });
  };

  // ✅ Show empty state if no data
  if (!data || data.length === 0) {
    return (
      <div className="registration-trend-container">
        <div className="chart-header">
          <div>
            <h2 className="chart-title">Registration Trend</h2>
            <p className="chart-subtitle">Daily registration activity over the last 30 days</p>
          </div>
          <div className="chart-legend">
            <div className="legend-item">
              <div className="legend-color" />
              <span>Registrations</span>
            </div>
          </div>
        </div>
        <div className="chart-empty-state">
          <p>No registration data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="registration-trend-container">
      <div className="chart-header">
        <div>
          <h2 className="chart-title">Registration Trend</h2>
          <p className="chart-subtitle">Daily registration activity over the last 30 days</p>
        </div>
        <div className="chart-legend">
          <div className="legend-item">
            <div className="legend-color" />
            <span>Registrations</span>
          </div>
        </div>
      </div>
      
      <div 
        ref={chartRef}
        className="chart-wrapper"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        role="img"
        aria-label="Line chart showing registration trends over time. Hover or tap on data points for details."
      >
        <svg 
          width="100%" 
          height={height} 
          viewBox={`0 0 ${width} ${height}`} 
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Grid lines */}
          {gridLines}
          
          {/* Y-axis labels */}
          {yLabels}
          
          {/* X-axis labels (responsive: show every other on mobile) */}
          <g fontSize="11" fill="#64748b" textAnchor="middle" fontFamily="Inter, -apple-system, sans-serif">
            {dates.map((date, i) => {
              if (isMobile && i % 2 !== 0) return null;
              const x = PADDING.left + (i * (innerWidth / Math.max(1, dates.length - 1)));
              return (
                <text 
                  key={`x-${i}`} 
                  x={x} 
                  y={height - PADDING.bottom + 18}
                  className="x-axis-label"
                >
                  {date.split(' ')[1]}
                </text>
              );
            })}
          </g>
          
          {/* Area under line (subtle gradient) - ✅ Using real data */}
          {points.length > 0 && (
            <path 
              d={`${pathD} L ${points[points.length - 1].x},${height - PADDING.bottom} L ${points[0].x},${height - PADDING.bottom} Z`}
              fill="url(#areaGradient)"
              opacity="0.12"
            />
          )}
          
          {/* Trend line - ✅ Using real data */}
          {points.length > 0 && (
            <path 
              d={pathD}
              fill="none"
              stroke="url(#lineGradient)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              filter="url(#glow)"
            />
          )}
          
          {/* Data points with hover effect - ✅ Using real data */}
          {points.map((point, i) => (
            <g key={`point-${i}`}>
              <circle
                cx={point.x}
                cy={point.y}
                r="5"
                fill="#ffffff"
                stroke="#3b82f6"
                strokeWidth="2.5"
                filter="url(#pointGlow)"
              />
              <circle
                cx={point.x}
                cy={point.y}
                r="3"
                fill="#3b82f6"
              />
            </g>
          ))}
          
          {/* Gradients and filters */}
          <defs>
            <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#2563eb" />
              <stop offset="100%" stopColor="#3b82f6" />
            </linearGradient>
            <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.05" />
            </linearGradient>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="pointGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          
          {/* X and Y axes */}
          <line 
            x1={PADDING.left} 
            y1={height - PADDING.bottom} 
            x2={width - PADDING.right} 
            y2={height - PADDING.bottom} 
            stroke="#94a3b8" 
            strokeWidth="1.5" 
          />
          <line 
            x1={PADDING.left} 
            y1={PADDING.top} 
            x2={PADDING.left} 
            y2={height - PADDING.bottom} 
            stroke="#94a3b8" 
            strokeWidth="1.5" 
          />
        </svg>
        
        {/* Professional tooltip */}
        {tooltip.visible && tooltip.data && (
          <div 
            ref={tooltipRef}
            className="chart-tooltip"
            style={{ 
              left: `${tooltip.x}px`, 
              top: `${tooltip.y}px`,
              transform: isMobile ? 'translateX(-50%)' : 'none'
            }}
            role="tooltip"
            aria-live="polite"
          >
            <div className="tooltip-header">
              <div className="tooltip-dot" />
              <span className="tooltip-date">{tooltip.data.date}</span>
            </div>
            <div className="tooltip-value">{tooltip.data.value.toLocaleString()}</div>
            <div className="tooltip-label">New Registrations</div>
            {!isMobile && <div className="tooltip-arrow" />}
          </div>
        )}
      </div>
      
      {/* Statistics - ✅ Calculated from real data */}
      <div className="chart-footer">
        <div className="chart-stats">
          <div>
            <span className="stat-label">Peak Day:</span>
            <span className="stat-value">
              {values.length > 0 ? dates[values.indexOf(Math.max(...values))] : 'N/A'}
            </span>
          </div>
          <div>
            <span className="stat-label">Total:</span>
            <span className="stat-value">{totalCount.toLocaleString()}</span>
          </div>
          <div>
            <span className="stat-label">Avg/Day:</span>
            <span className="stat-value">
              {values.length > 0 ? (totalCount / values.length).toFixed(1) : '0.0'}
            </span>
          </div>
        </div>
        <div className="chart-note">Data updated daily at 2:00 AM UTC</div>
      </div>
    </div>
  );
};

export default RegistrationTrendChart;